import { useCallback, useEffect, useState } from 'react';
import { isCancelled, toApiError } from '../../services/pocApi';

/**
 * Loads data with loader(signal) whenever `deps` change or reload() is called.
 * A newer request aborts the older one, so a slow, stale response can never
 * overwrite fresher data. While loading, the previous data is kept but
 * `loading` is true, so screens never show zero counts as if they were real.
 */
export default function useApiResource(loader, deps) {
    const [state, setState] = useState({ data: null, error: null, loading: true });
    const [version, setVersion] = useState(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const load = useCallback(loader, deps);

    useEffect(() => {
        const controller = new AbortController();
        setState((s) => ({ ...s, error: null, loading: true }));
        load(controller.signal)
            .then((data) => {
                if (!controller.signal.aborted) setState({ data, error: null, loading: false });
            })
            .catch((error) => {
                if (controller.signal.aborted || isCancelled(error)) return;
                setState({ data: null, error: toApiError(error), loading: false });
            });
        return () => controller.abort();
    }, [load, version]);

    const reload = useCallback(() => setVersion((v) => v + 1), []);
    return { ...state, reload };
}
